<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import OButton from '@/components/ui/OButton.vue'
import OField from '@/components/ui/OField.vue'
import OInput from '@/components/ui/OInput.vue'
import OModal from '@/components/ui/OModal.vue'
import OSelect from '@/components/ui/OSelect.vue'

const { t } = useI18n()

/**
 * The re-authentication prompt for a destructive operation.
 *
 * Design §11.4: *"Destructive operations (core update, extension removal, file quarantine) require
 * re-authentication and are recorded in an append-only audit log."*
 *
 * Mounted once, at the application shell, and driven by `auth.reauthRequest`. That keeps the
 * prompt in one place instead of one copy per screen that could dispatch something destructive —
 * and a security control with several implementations has several chances to be the wrong one.
 *
 * The token this produces authorises **one capability**, named in the request, for minutes. It
 * does not widen the session and it is never stored.
 */

const auth = useAuthStore()

const factor = ref<'password' | 'totp' | 'webauthn'>('totp')
const password = ref('')
const code = ref('')

const open = computed(() => auth.reauthRequest !== null)

watch(open, (isOpen) => {
  if (!isOpen) {
    password.value = ''
    code.value = ''
    factor.value = 'totp'
  }
})

const factorOptions = [
  { value: 'totp', label: 'Authenticator code' },
  { value: 'webauthn', label: 'Security key' },
  { value: 'password', label: 'Password' },
]

const canSubmit = computed(() => {
  if (factor.value === 'password') return password.value !== ''
  return code.value.trim() !== ''
})

const submit = async (): Promise<void> => {
  try {
    // The core takes one pair — `{method, response}` — whatever the factor is. It does not take a
    // separate `password` and `code`; `response` is "the password, the TOTP code, or the
    // serialised WebAuthn assertion", in its own words.
    await auth.completeReauth({
      method: factor.value,
      response: factor.value === 'password' ? password.value : code.value.trim(),
    })
  } catch {
    // The store holds the message; the dialog stays open so the person can try again.
  }
}
</script>

<template>
  <OModal
    :open="open"
    :title="t('capabilities.identityTitle')"
    :subtitle="auth.reauthRequest?.label"
    size="sm"
    :busy="auth.busy"
    @close="auth.cancelReauth()"
  >
    <div class="stack">
      <p class="why">
        {{ t('capabilities.reauthExplain') }}
      </p>

      <p class="scope">
        <i18n-t keypath="capabilities.authorises" tag="span">
          <template #action><code>{{ auth.reauthRequest?.capability }}</code></template>
        </i18n-t>
      </p>

      <OField v-slot="{ id }" :label="t('capabilities.factorLabel')">
        <OSelect :id="id" v-model="factor" :options="factorOptions" />
      </OField>

      <OField
        v-if="factor === 'password'"
        v-slot="{ id }"
        :label="t('capabilities.passwordLabel')"
      >
        <OInput
          :id="id"
          v-model="password"
          type="password"
          autocomplete="current-password"
          @enter="submit"
        />
      </OField>

      <OField
        v-else
        v-slot="{ id, describedBy }"
        :label="t('capabilities.codeLabel')"
        :help="
          factor === 'webauthn'
            ? 'Touch your security key, or paste the assertion your browser produced.'
            : 'The six digits from your authenticator app.'
        "
      >
        <OInput
          :id="id"
          v-model="code"
          :described-by="describedBy"
          :inputmode="factor === 'totp' ? 'numeric' : 'text'"
          autocomplete="one-time-code"
          @enter="submit"
        />
      </OField>

      <p v-if="auth.error" class="error" role="alert">{{ auth.error }}</p>
    </div>

    <template #footer>
      <OButton variant="ghost" :disabled="auth.busy" @click="auth.cancelReauth()">{{ t('capabilities.cancel') }}</OButton>
      <OButton variant="danger" :disabled="!canSubmit" :loading="auth.busy" @click="submit">
        {{ t('capabilities.confirm') }}
      </OButton>
    </template>
  </OModal>
</template>

<style scoped>
.why {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-wrap: pretty;
}

.scope {
  font-size: var(--text-xs);
  color: var(--text-default);
}

.error {
  font-size: var(--text-xs);
  color: var(--critical);
}
</style>
